/**
 * ────────────────────────────────────────────────────────────────
 *  PRIMUM COLOR DESIGN TOKENS
 * ────────────────────────────────────────────────────────────────
 * Single source of truth for all app colors. Supports light/dark themes.
 * Part of complete design system (colors + typography + shadows in tailwind.config.js).
 *
 * DESIGN SYSTEM STRUCTURE
 * • BASE_COLORS    – utilities (transparent, black, white, gray)
 * • LIGHT_COLORS   – complete brand palette for light theme
 * • DARK_COLORS    – same semantic keys, optimized for dark theme
 *
 * COLOR CATEGORIES
 * • Brand colors: primary (green), secondary (blue), tertiary (orange)
 * • Semantic colors: error, success, warning, info
 * • System colors: typography, outline, background, indicator
 * • Color scale: 0-950 (lighter to darker), 500 = primary brand shade
 *
 * TAILWIND CSS INTEGRATION
 * • Colors exported from this file → mapped to CSS variables (--color-primary-500)
 * • Tailwind config references variables → enables dynamic theming
 * • Available classes: bg-primary-500, text-secondary-300, border-error-400
 * ────────────────────────────────────────────────────────────────
 */
export const BASE_COLORS = {
  inherit: "inherit",
  current: "currentColor",
  transparent: "transparent",
  black: "#000000",
  white: "#ffffff",
  gray: "#8b8680",
} as const;

export const DARK_COLORS = {
  primary: {
    0: "#000000",
    50: "#000F08",
    100: "#12221E",
    200: "#253531",
    300: "#394945",
    400: "#4D5F5A",
    500: "#637570",
    600: "#798C87",
    700: "#A9BCB7",
    800: "#C1D5CF",
    900: "#DEEFEA",
    950: "#EAF6F2",
  },
  secondary: {
    0: "#000f1a",
    50: "#08222d",
    100: "#1d3541",
    200: "#324955",
    300: "#475f6b",
    400: "#5d7582",
    500: "#748c99",
    600: "#8ba4b1",
    700: "#a3bcca",
    800: "#bbd5e3",
    900: "#d5eefd",
    950: "#efffff",
  },
  tertiary: {
    0: "#3D150D",
    50: "#722E1C",
    100: "#8D351F",
    200: "#B1411D",
    300: "#D55721",
    400: "#E36B26",
    500: "#E98D4E",
    600: "#F0B581",
    700: "#F6D3B2",
    800: "#FBEAD9",
    900: "#FDF6EF",
    950: "#FFF9F5",
  },
  error: {
    0: "#531313",
    50: "#7f1d1d",
    100: "#991b1b",
    200: "#b91c1c",
    300: "#dc2626",
    400: "#e63535",
    500: "#ef4444",
    600: "#f87171",
    700: "#fca5a5",
    800: "#fecaca",
    900: "#fee2e2",
    950: "#fee9e9",
  },
  success: {
    0: "#1b3224",
    50: "#14532d",
    100: "#166534",
    200: "#206f3e",
    300: "#2a7948",
    400: "#348352",
    500: "#489766",
    600: "#66b584",
    700: "#84d3a2",
    800: "#a2f1c0",
    900: "#caffe8",
    950: "#e4fff4",
  },
  warning: {
    0: "#542d12",
    50: "#6c3813",
    100: "#824417",
    200: "#b45a1a",
    300: "#d76c1f",
    400: "#e77828",
    500: "#fb954b",
    600: "#fdad74",
    700: "#fecdaa",
    800: "#ffe7d5",
    900: "#fff9f5",
    950: "#fffdfb",
  },
  info: {
    0: "#032638",
    50: "#05405d",
    100: "#075a83",
    200: "#0973a8",
    300: "#0b8dcd",
    400: "#0da6f2",
    500: "#32b4f4",
    600: "#57c2f6",
    700: "#7ccff8",
    800: "#a2ddfa",
    900: "#c7ebfc",
    950: "#ecf8fe",
  },
  typography: {
    0: "#171717",
    50: "#262627",
    100: "#404040",
    200: "#525252",
    300: "#737373",
    400: "#8c8c8c",
    500: "#a3a3a3",
    600: "#d4d4d4",
    700: "#dbdbdc",
    800: "#e5e5e5",
    900: "#f5f5f5",
    950: "#fefeff",
    white: "#ffffff",
    gray: "#d4d4d4",
    black: "#181718",
  },
  outline: {
    0: "#1a1717",
    50: "#272624",
    100: "#414141",
    200: "#535252",
    300: "#737474",
    400: "#8c8d8d",
    500: "#a5a3a3",
    600: "#d3d3d3",
    700: "#dddcdb",
    800: "#e6e6e6",
    900: "#f3f3f3",
    950: "#fdfefe",
  },
  background: {
    0: "#121212",
    50: "#272625",
    100: "#414040",
    200: "#535252",
    300: "#747474",
    400: "#8e8e8e",
    500: "#a2a3a3",
    600: "#d5d4d4",
    700: "#dcdbdb",
    800: "#f2f1f1",
    900: "#f6f6f6",
    950: "#fefefe",
    error: "#422b2b",
    warning: "#412f23",
    muted: "#333333",
    success: "#1c2b21",
    info: "#1a282e",
    light: "#ffffff",
    alice: "#f9fbfc",
    dark: "#121212",
  },
  indicator: {
    primary: "#f7f7f7",
    info: "#a1c7f5",
    error: "#e84645",
  },
  highlight: {
    0: "#3D150D",
    50: "#722E1C",
    100: "#8D351F",
    200: "#B1411D",
    300: "#D55721",
    400: "#E36B26",
    500: "#E98D4E",
    600: "#F0B581",
    700: "#F6D3B2",
    800: "#FBEAD9",
    900: "#FDF6EF",
    950: "#FFF9F5",
  },
  mark: {
    0: "#1C1A07",
    50: "#332F0E",
    100: "#4D4816",
    200: "#6B641E",
    300: "#8A8226",
    400: "#A89E2F",
    500: "#C4B93E",
    600: "#D9D061",
    700: "#E8E28F",
    800: "#F3F0BB",
    900: "#F9F7DC",
    950: "#FDFCF0",
  },
} as const;

export const LIGHT_COLORS = {
  primary: {
    0: "#EAF6F2",
    50: "#DEEFEA",
    100: "#C1D5CF",
    200: "#A9BCB7",
    300: "#91A49E",
    400: "#798C87",
    500: "#637570",
    600: "#4D5F5A",
    700: "#394945",
    800: "#253531",
    900: "#12221E",
    950: "#080808",
  },
  secondary: {
    0: "#efffff",
    50: "#d5eefd",
    100: "#bbd5e3",
    200: "#a3bcca",
    300: "#8ba4b1",
    400: "#748c99",
    500: "#5d7582",
    600: "#475f6b",
    700: "#324955",
    800: "#1d3541",
    900: "#08222d",
    950: "#000f1a",
  },
  tertiary: {
    0: "#FFF9F5",
    50: "#FDF6EF",
    100: "#FBEAD9",
    200: "#F6D3B2",
    300: "#F0B581",
    400: "#E98D4E",
    500: "#E36B26",
    600: "#D55721",
    700: "#B1411D",
    800: "#8D351F",
    900: "#722E1C",
    950: "#3D150D",
  },
  error: {
    0: "#fee9e9",
    50: "#fee2e2",
    100: "#fecaca",
    200: "#fca5a5",
    300: "#f87171",
    400: "#ef4444",
    500: "#e63535",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#531313",
  },
  success: {
    0: "#e4fff4",
    50: "#caffe8",
    100: "#a2f1c0",
    200: "#84d3a2",
    300: "#66b584",
    400: "#489766",
    500: "#348352",
    600: "#2a7948",
    700: "#206f3e",
    800: "#166534",
    900: "#14532d",
    950: "#1b3224",
  },
  warning: {
    0: "#fffdfb",
    50: "#fff9f5",
    100: "#ffe7d5",
    200: "#fecdaa",
    300: "#fdad74",
    400: "#fb954b",
    500: "#e77828",
    600: "#d76c1f",
    700: "#b45a1a",
    800: "#824417",
    900: "#6c3813",
    950: "#542d12",
  },
  info: {
    0: "#ecf8fe",
    50: "#c7ebfc",
    100: "#a2ddfa",
    200: "#7ccff8",
    300: "#57c2f6",
    400: "#32b4f4",
    500: "#0da6f2",
    600: "#0b8dcd",
    700: "#0973a8",
    800: "#075a83",
    900: "#05405d",
    950: "#032638",
  },
  typography: {
    0: "#fefeff",
    50: "#f5f5f5",
    100: "#e5e5e5",
    200: "#dbdbdc",
    300: "#d4d4d4",
    400: "#a3a3a3",
    500: "#8c8c8c",
    600: "#737373",
    700: "#525252",
    800: "#404040",
    900: "#262627",
    950: "#171717",
    white: "#ffffff",
    gray: "#d4d4d4",
    black: "#181718",
  },
  outline: {
    0: "#fdfefe",
    50: "#f3f3f3",
    100: "#e6e6e6",
    200: "#dddcdb",
    300: "#d3d3d3",
    400: "#a5a3a3",
    500: "#8c8d8d",
    600: "#737474",
    700: "#535252",
    800: "#414141",
    900: "#272624",
    950: "#1a1717",
  },
  background: {
    0: "#ffffff",
    50: "#f6f6f6",
    100: "#f2f1f1",
    200: "#dcdbdb",
    300: "#d5d4d4",
    400: "#a2a3a3",
    500: "#8e8e8e",
    600: "#747474",
    700: "#535252",
    800: "#414040",
    900: "#272625",
    950: "#181718",
    error: "#fef1f1",
    warning: "#fff4eb",
    muted: "#f7f8f7",
    success: "#edfcf2",
    info: "#ebf8fe",
    light: "#ffffff",
    alice: "#f9fbfc",
    dark: "#121212",
  },
  indicator: {
    primary: "#373737",
    info: "#5399ec",
    error: "#b91c1c",
  },
  highlight: {
    0: "#FFF9F5",
    50: "#FDF6EF",
    100: "#FBEAD9",
    200: "#F6D3B2",
    300: "#F0B581",
    400: "#E98D4E",
    500: "#E36B26",
    600: "#D55721",
    700: "#B1411D",
    800: "#8D351F",
    900: "#722E1C",
    950: "#3D150D",
  },
  mark: {
    0: "#FDFCF0",
    50: "#F9F7DC",
    100: "#F3F0BB",
    200: "#E8E28F",
    300: "#D9D061",
    400: "#C4B93E",
    500: "#A89E2F",
    600: "#8A8226",
    700: "#6B641E",
    800: "#4D4816",
    900: "#332F0E",
    950: "#1C1A07",
  },
} as const;
